import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Modal,
  Row,
  Select,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ReferenceArea,
  Label,
} from 'recharts';

import { Server } from '../../../../../../../api/server-index';
import { AuthContext } from '../../../../../../../context/auth-context';
import { SemesterAttendanceStatsResponseDto } from '../../../../../../../types/dto/statistics.dto';
import { SubjectDto } from '../../../../../../../types/dto/subject.dto';
import MonthDetails from './month-details/month-details.component';

interface Props {
  lecturerPin: string;
}

const AttendanceStatistics = ({ lecturerPin }: Props) => {
  const { t } = useTranslation(['user']);
  const { language } = useContext(AuthContext);
  const [offset, setOffset] = useState<number>(0);
  const [lecturerSubjects, setLecturerSubjects] = useState<SubjectDto[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<SubjectDto>();
  const [loading, setLoading] = useState<boolean>(true);
  const [attendanceStats, setAttendanceStats] =
    useState<SemesterAttendanceStatsResponseDto>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMonthData, setSelectedMonthData] = useState<
    SemesterAttendanceStatsResponseDto['monthlyAttendance'][0] | null
  >(null);

  const fetchLecturerSubjects = useCallback(async () => {
    let response: SubjectDto[];
    response = await Server.Subjects.getSubjectsFlat({
      lecturerPin,
      moodleOnly: true,
    });
    if (response.length < 1) {
      response = await Server.Subjects.getSubjectsFlat({
        moodleOnly: true,
      });
    }

    setSelectedSubject(response[0]);
    setLecturerSubjects(response);
  }, [lecturerPin]);

  const fetchStats = useCallback(async () => {
    if (selectedSubject) {
      setLoading(true);
      const response = await Server.Statistics.getSemesterStatistics(
        selectedSubject.id,
        lecturerPin,
        offset
      );

      setAttendanceStats(response);
      setLoading(false);
    }
  }, [lecturerPin, offset, selectedSubject]);

  useEffect(() => {
    fetchLecturerSubjects();
  }, [fetchLecturerSubjects]);

  useEffect(() => {
    if (selectedSubject) {
      fetchStats();
    }
  }, [selectedSubject, fetchStats]);

  const filterOption = (
    input: string,
    option?: { label: string; value: string }
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const getMonthRangeLabel = () => {
    const endMonth = dayjs();
    const startMonth = endMonth.subtract(5 + offset, 'month');
    const adjustedEnd = endMonth.subtract(offset, 'month');
    return `${startMonth.format('MMMM YYYY.')} – ${adjustedEnd.format(
      'MMMM YYYY.'
    )}`;
  };

  return (
    <Card loading={loading} className='shadow-sm' style={{ minHeight: 620 }}>
      <Alert
        type='info'
        showIcon
        description={t('attendance_stats.infoText')}
      />
      <Row gutter={[{ xs: 0, sm: 16 }, 32]} style={{ marginTop: 8 }}>
        <Col xl={8} md={8} sm={8} xs={24}>
          <div
            className='text-gray'
            style={{ marginLeft: 4, fontWeight: 500, fontSize: 12 }}
          >
            {t('attendance_stats.subject')}
          </div>
          <Select
            showSearch
            filterOption={filterOption}
            defaultValue={selectedSubject?.id}
            value={selectedSubject?.id}
            options={lecturerSubjects.map((ls) => ({
              label:
                language === 'en'
                  ? ls.subjectNameEn ?? ls.subjectName
                  : ls.subjectName,
              value: ls.id,
            }))}
            onChange={(value) =>
              setSelectedSubject(lecturerSubjects.find((ls) => ls.id === value))
            }
            style={{ width: '100%' }}
          />
        </Col>
        <Col span={24}>
          <Flex align='center' justify='center' gap={6}>
            <Tooltip title={t('attendance_stats.6_mo_earlier')}>
              <Button
                size='small'
                icon={<DoubleLeftOutlined />}
                onClick={() => setOffset((prev) => Math.min(prev + 6, 6))}
                disabled={offset >= 6}
              />
            </Tooltip>
            <Tooltip title={t('attendance_stats.mo_earlier')}>
              <Button
                size='small'
                icon={<LeftOutlined />}
                onClick={() => setOffset((prev) => Math.min(prev + 1, 6))}
                disabled={offset >= 6}
              />
            </Tooltip>

            <div
              style={{
                minWidth: 150,
                textAlign: 'center',
                fontWeight: 500,
                margin: '0 8px',
              }}
            >
              {getMonthRangeLabel()}
            </div>

            <Tooltip title={t('attendance_stats.mo_later')}>
              <Button
                size='small'
                icon={<RightOutlined />}
                onClick={() => setOffset((prev) => Math.max(prev - 1, 0))}
                disabled={offset <= 0}
              />
            </Tooltip>
            <Tooltip title={t('attendance_stats.6_mo_later')}>
              <Button
                size='small'
                icon={<DoubleRightOutlined />}
                onClick={() => setOffset((prev) => Math.max(prev - 6, 0))}
                disabled={offset <= 0}
              />
            </Tooltip>
          </Flex>
        </Col>

        <Col span={24}>
          <div className='mt-2'>
            {attendanceStats && (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={12}>
                  <ResponsiveContainer width='100%' height={300}>
                    <BarChart
                      data={attendanceStats.monthlyAttendance}
                      onClick={(data) => {
                        if (data && data.activeLabel) {
                          const clickedMonth =
                            attendanceStats.monthlyAttendance.find(
                              (monthData) =>
                                monthData.month === data.activeLabel
                            );
                          if (clickedMonth && clickedMonth.events.length > 0) {
                            setSelectedMonthData(clickedMonth);
                            setIsModalOpen(true);
                          }
                        }
                      }}
                    >
                      <defs>
                        <linearGradient
                          id='attendanceGradient'
                          x1='0'
                          y1='0'
                          x2='0'
                          y2='1'
                        >
                          <stop offset='0%' stopColor='var(--secondary)' />
                          <stop offset='80%' stopColor='var(--primary)' />
                          <stop offset='100%' stopColor='var(--primary)' />
                        </linearGradient>
                      </defs>

                      <XAxis
                        dataKey='month'
                        angle={-45}
                        textAnchor='end'
                        height={60}
                        tickFormatter={(val: string) =>
                          dayjs(val).format("MMM 'YY.")
                        }
                      />
                      <YAxis
                        domain={[0, 100]}
                        tickFormatter={(val) => `${val}%`}
                      />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            if (data.events.length === 0) {
                              return (
                                <div
                                  className='custom-tooltip'
                                  style={{
                                    background: '#fff',
                                    padding: '10px',
                                    border: '1px solid #ccc',
                                  }}
                                >
                                  <p className='label'>
                                    {t('attendance_stats.no_events_scheduled')}
                                  </p>
                                </div>
                              );
                            }
                            return (
                              <div
                                className='custom-tooltip'
                                style={{
                                  background: '#fff',
                                  padding: '10px',
                                  border: '1px solid #ccc',
                                }}
                              >
                                <p className='label'>{`${
                                  payload[0].name
                                } : ${payload[0].value.toFixed(2)}%`}</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      {attendanceStats.monthlyAttendance.map((entry) => {
                        if (entry.events.length === 0) {
                          return (
                            <ReferenceArea
                              key={`ref-${entry.month}`}
                              x1={entry.month}
                              x2={entry.month}
                              y1={0}
                              y2={100}
                              stroke='none'
                              fill='#f9f9f9'
                              ifOverflow='visible'
                            >
                              <Label
                                value={t('attendance_stats.no_events')}
                                angle={-90}
                                position='center'
                                fill='#a0a0a0'
                                fontSize={12}
                                style={{ textAnchor: 'middle' }}
                              />
                            </ReferenceArea>
                          );
                        }
                        return null;
                      })}
                      <Bar
                        dataKey='monthAttendance'
                        name={t('attendance_stats.attendance')}
                        fill='url(#attendanceGradient)'
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Col>
                <Col xs={24} sm={12} md={12}>
                  <ResponsiveContainer width='100%' height={250}>
                    <PieChart>
                      {attendanceStats &&
                      attendanceStats.monthlyAttendance.every(
                        (month) => month.events.length === 0
                      ) ? (
                        <Pie
                          data={[
                            {
                              name: t('attendance_stats.no_events'),
                              value: 100,
                            },
                          ]}
                          dataKey='value'
                          cx='50%'
                          cy='50%'
                          outerRadius={80}
                          fill='#f0f0f0'
                          labelLine={false}
                          label={({ cx, cy }) => (
                            <text
                              x={cx}
                              y={cy}
                              fill='#a0a0a0'
                              textAnchor='middle'
                              dominantBaseline='central'
                              fontSize='14'
                            >
                              {t('attendance_stats.no_events')}
                            </text>
                          )}
                        />
                      ) : (
                        <>
                          <Pie
                            data={[
                              {
                                name: t('attendance_stats.attended'),
                                value: attendanceStats.semesterAttendance,
                              },
                              {
                                name: t('attendance_stats.not_attended'),
                                value: 100 - attendanceStats.semesterAttendance,
                              },
                            ]}
                            dataKey='value'
                            nameKey='name'
                            cx='50%'
                            cy='50%'
                            outerRadius={80}
                            label={({ _, percent }) =>
                              `${(percent! * 100).toFixed(2)}%`
                            }
                          >
                            <Cell fill='var(--green)' />
                            <Cell fill='var(--red)' />
                          </Pie>
                        </>
                      )}
                    </PieChart>
                  </ResponsiveContainer>
                </Col>
              </Row>
            )}
          </div>
        </Col>
      </Row>
      <Modal
        title={
          <Flex vertical>
            <span>
              {t('attendance_stats.detailed.title')}{' '}
              {dayjs(selectedMonthData?.month).format("MMMM 'YY")}
            </span>
            <span style={{ fontSize: 12 }} className='text-gray'>
              {language === 'en'
                ? selectedSubject?.subjectNameEn ?? selectedSubject?.subjectName
                : selectedSubject?.subjectName}
            </span>
          </Flex>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={'90vw'}
      >
        {selectedMonthData && selectedMonthData.events.length > 0 ? (
          <MonthDetails selectedMonthData={selectedMonthData} />
        ) : (
          <p style={{ textAlign: 'center' }}>
            {t('attendance_stats.detailed.no_data')}
          </p>
        )}
      </Modal>
    </Card>
  );
};

export default AttendanceStatistics;
